import { memo } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronRightIcon,
  FolderIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

interface QuickActionsProps {
  /** 사용자 플랜 (free / growth / scale / enterprise) */
  plan?: string
}

/** 팀 협업은 Growth 이상에서만 제공된다 */
const TEAM_ENABLED_PLANS = ['growth', 'scale', 'enterprise']

function PlanBadge({ plan }: { plan?: string }) {
  const { t } = useTranslation()
  if (!plan) {
    return null
  }

  if (!TEAM_ENABLED_PLANS.includes(plan)) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
        {t('dashboard.planRequired')}
      </span>
    )
  }

  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
      {t('dashboard.planLabel', { plan: plan.toUpperCase() })}
    </span>
  )
}

/** 대시보드 바로가기 카드 */
function QuickActions({ plan }: QuickActionsProps) {
  const { t } = useTranslation()

  const items = [
    {
      to: '/bookmarks',
      Icon: FolderIcon,
      title: t('dashboard.manageLinks'),
      hint: t('dashboard.manageLinksHint'),
      cta: t('dashboard.bookmarkButton'),
      badge: null as ReactNode,
    },
    {
      to: '/team-collaboration',
      Icon: UserGroupIcon,
      title: t('dashboard.teamTitle'),
      hint: t('dashboard.teamHint'),
      cta: t('dashboard.teamButton'),
      badge: <PlanBadge plan={plan} />,
    },
  ]

  // 좁은 칸에 들어가므로 가로로 나누지 않는다. 한 카드 안에 줄로 쌓는다.
  return (
    <section className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      {items.map(({ to, Icon, title, hint, cta, badge }, index) => (
        <Link
          key={to}
          to={to}
          className={`flex items-center gap-3.5 px-5 py-4 transition hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
            index > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''
          }`}
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-primary-50 text-primary-600 dark:bg-primary-950/60 dark:text-primary-400">
            <Icon className="h-5 w-5" />
          </span>

          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-1.5 text-[15px] font-bold text-gray-900 dark:text-white">
              {title}
              {badge}
            </span>
            <span className="text-[13px] text-gray-500 dark:text-gray-400">
              {hint}
            </span>
          </span>

          <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-[13px] font-semibold text-primary-600 dark:text-primary-400">
            {cta}
            <ChevronRightIcon className="h-4 w-4" />
          </span>
        </Link>
      ))}
    </section>
  )
}

/** 모달 상태가 바뀌어도 값이 같으면 다시 그리지 않는다 */
export default memo(QuickActions)
